import React from 'react';
import { APP_BG, APP_HEX } from '@/constant/theme/colors.constant';

interface Props {
  rows?: number;
  cols: number;
  hasExpand: boolean;
  /** Width class string per column (e.g. 'w-16') — mirrors real column widths to prevent reflow */
  columnWidths?: (string | undefined)[];
}

// Per-row width variation so skeleton looks like real heterogeneous data
// Two alternating patterns — wide/narrow to mimic UUID vs short fields
const WIDE_WIDTHS = [72, 68, 42, 78, 36, 38, 30, 55, 48, 40, 60, 35];
const NARROW_WIDTHS = [55, 50, 36, 60, 30, 28, 24, 42, 38, 30, 45, 28];

export const SkeletonRows = ({
  rows = 15,
  cols,
  hasExpand,
  columnWidths = [],
}: Props) => {
  return (
    <>
      <style>{`
                @keyframes skWave {
                    0%   { transform: translateX(-100%); }
                    60%  { transform: translateX(100%); }
                    100% { transform: translateX(100%); }
                }
                .sk-bar {
                    position: relative;
                    overflow: hidden;
                    background-color: ${APP_HEX.skeletonBar};
                    border-radius: 9999px;
                }
                .sk-pill {
                    position: relative;
                    overflow: hidden;
                    background-color: ${APP_HEX.skeletonPill};
                    border-radius: 9999px;
                    height: 22px;
                    width: 72px;
                }
                .sk-circle {
                    position: relative;
                    overflow: hidden;
                    background-color: ${APP_HEX.skeletonCircle};
                    border-radius: 9999px;
                }
                .sk-bar::after, .sk-pill::after, .sk-circle::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255,127,48,0.14) 50%,
                        transparent 100%
                    );
                    animation: skWave 1.6s ease-in-out var(--sk-delay, 0s) infinite;
                }
                .dark .sk-bar   { background-color: ${APP_HEX.skeletonDarkBar}; }
                .dark .sk-pill  { background-color: ${APP_HEX.skeletonDarkPill}; }
                .dark .sk-circle { background-color: ${APP_HEX.skeletonDarkCircle}; }
                .dark .sk-bar::after, .dark .sk-pill::after, .dark .sk-circle::after {
                    background: linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255,127,48,0.22) 50%,
                        transparent 100%
                    );
                }
            `}</style>

      {Array.from({ length: rows }).map((_, ri) => {
        // Alternate width pattern per row for natural variety
        const widths = ri % 2 === 0 ? WIDE_WIDTHS : NARROW_WIDTHS;
        // Every 3rd row gets a slightly shorter first column (shorter ID)
        const firstColW = ri % 3 === 0 ? 52 : widths[0];

        return (
          <tr
            key={ri}
            className={[
              'border-b border-gray-100 dark:border-gray-700/40',
              ri % 2 === 0 ? APP_BG.base : APP_BG.canvas,
            ].join(' ')}
          >
            {/* Data cells — matching real row px-4 py-3.5 */}
            {Array.from({ length: cols }).map((_, ci) => {
              const maxW =
                ci === 0
                  ? firstColW
                  : ci < widths.length
                    ? widths[ci]
                    : widths[ci % widths.length];
              const delay = `${(ri * 0.06 + ci * 0.04).toFixed(3)}s`;
              const colWidthClass = columnWidths[ci] ?? '';

              // Columns 4–6 (typically badge/pill columns) → pill shape
              // Columns 0–3 and 7+ → regular bar
              const isPillCol = ci >= 4 && ci <= 6;

              return (
                <td
                  key={ci}
                  className={['px-4 py-3.5', colWidthClass]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {isPillCol ? (
                    <div
                      className="sk-pill"
                      style={{ '--sk-delay': delay } as React.CSSProperties}
                    />
                  ) : (
                    <div
                      className="sk-bar h-3.5 w-full"
                      style={
                        {
                          maxWidth: `${maxW}%`,
                          '--sk-delay': delay,
                        } as React.CSSProperties
                      }
                    />
                  )}
                </td>
              );
            })}

            {/* Expand chevron placeholder — RIGHT side, matching real row */}
            {hasExpand && (
              <td className="w-10 px-3 py-3.5 text-center align-middle">
                <div
                  className="sk-circle h-6 w-6 mx-auto"
                  style={
                    {
                      '--sk-delay': `${(ri * 0.06).toFixed(3)}s`,
                    } as React.CSSProperties
                  }
                />
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
};
